import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import { useAdminTranslation } from '../../utils/useAdminTranslation';
import { translateCategory, translateCountry } from '../../utils/translations';
import { ProductTable } from '../catalog/ProductTable';
import { ProductDetailDialog } from '../catalog/ProductDetailDialog';
import { PaginationControls } from '../catalog/PaginationControls';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ScrollArea } from '../ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import { 
  Filter, 
  LayoutGrid, 
  List, 
  Loader2,
  Search,
  X,
  Globe,
  FolderOpen,
  RotateCcw,
  Download,
  ChevronDown,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export const ClientCatalogTab = () => {
  const { language } = useLanguage();
  const { tx } = useAdminTranslation();
  const [products, setProducts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [downloading, setDownloading] = useState(false);

  // Filter options (loaded from DB)
  const [uniqueCountries, setUniqueCountries] = useState([]);
  const [uniqueCategories, setUniqueCategories] = useState([]);

  // Active filters
  const [filters, setFilters] = useState({
    search: '',
    countries: [],
    categories: [],
    siteTypes: [],
    casino: false,
    cbd: false,
    gambling: false,
    cryptocurrency: false,
    sex: false,
    erotic: false,
    vpn: false,
    tarot: false,
    porn: false,
    cannabis: false,
    dr_min: '',
    dr_max: '',
    traffic_min: '',
    traffic_max: '',
    price_min: '',
    price_max: '',
  });

  // Country/Category search
  const [countrySearch, setCountrySearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  const texts = {
    es: {
      title: 'Vista Catálogo Cliente',
      subtitle: 'Así ven los clientes el catálogo de productos',
      catalog: 'Catálogo', results: 'resultados', search: 'Buscar por dominio...', searchLabel: 'Buscar',
      siteType: 'Tipo Medio',
      country: 'País', allCountries: 'Todos los países', category: 'Categoría', allCategories: 'Todas las categorías',
      sensitiveTopics: 'Temáticas Sensibles', searchCountry: 'Buscar país...', searchCategory: 'Buscar categoría...',
      resetFilters: 'Resetear Todo', casino: 'Casino', cbd: 'CBD', gambling: 'Apuestas', cryptocurrency: 'Criptomonedas',
      sex: 'Sexo', erotic: 'Erótico', vpn: 'VPN', tarot: 'Tarot', porn: 'Porno', cannabis: 'Cannabis',
      filters: 'Filtros', clearFilters: 'Limpiar Filtros', applyFilters: 'Aplicar filtros',
      noResults: 'No se encontraron resultados', tryOther: 'Intenta con otros filtros',
      page: 'Página', of: 'de', showing: 'Mostrando', activeFilters: 'filtros activos',
      dr: 'Domain Rating (DR)', drMin: 'DR mínimo', drMax: 'DR máximo',
      traffic: 'Tráfico Orgánico', trafficMin: 'Tráfico mínimo', trafficMax: 'Tráfico máximo',
      price: 'Precio', priceRange: 'Rango de Precio', minPrice: 'Precio mínimo', maxPrice: 'Precio máximo',
      itemsPerPage: 'por página', showingItems: 'Mostrando',
      countries: 'países', categories: 'categorías', rangeInfo: 'Rango de 0 a 100',
      downloadFiltered: 'Descargar Excel Filtrado', downloadAll: 'Descargar Todo',
      downloading: 'Descargando...',
    },
    en: {
      title: 'Client Catalog View',
      subtitle: 'This is how clients see the product catalog',
      catalog: 'Catalog', results: 'results', search: 'Search by domain...', searchLabel: 'Search',
      country: 'Country', allCountries: 'All countries', category: 'Category', allCategories: 'All categories',
      sensitiveTopics: 'Sensitive Topics', searchCountry: 'Search country...', searchCategory: 'Search category...',
      resetFilters: 'Reset All', casino: 'Casino', cbd: 'CBD', gambling: 'Gambling', cryptocurrency: 'Cryptocurrency',
      sex: 'Sex', erotic: 'Erotic', vpn: 'VPN', tarot: 'Tarot', porn: 'Porn', cannabis: 'Cannabis',
      filters: 'Filters', clearFilters: 'Clear Filters', applyFilters: 'Apply filters',
      noResults: 'No results found', tryOther: 'Try different filters',
      page: 'Page', of: 'of', showing: 'Showing', activeFilters: 'active filters',
      dr: 'Domain Rating (DR)', drMin: 'Min DR', drMax: 'Max DR',
      traffic: 'Organic Traffic', trafficMin: 'Min traffic', trafficMax: 'Max traffic',
      price: 'Price', priceRange: 'Price Range', minPrice: 'Min price', maxPrice: 'Max price',
      itemsPerPage: 'per page', showingItems: 'Showing',
      countries: 'countries', categories: 'categories', rangeInfo: 'Range from 0 to 100',
      downloadFiltered: 'Download Filtered Excel', downloadAll: 'Download All',
      downloading: 'Downloading...',
    },
  };

  const t = texts[language] || texts.es;

  // Fetch filter options
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await axios.get(`${API}/filters/options`);
        setUniqueCountries(response.data.countries || []);
        setUniqueCategories(response.data.categories || []);
      } catch (error) {
        console.error('Error fetching filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch products
  const fetchProducts = useCallback(async (page = 1, customFilters = null, limit = itemsPerPage) => {
    setLoading(true);
    try {
      const f = customFilters || filters;
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (f.search) params.append('search', f.search);
      if (f.countries && f.countries.length > 0) {
        params.append('countries', f.countries.join(','));
      }
      if (f.categories && f.categories.length > 0) {
        params.append('categories', f.categories.join(','));
      }
      if (f.siteTypes && f.siteTypes.length > 0) {
        params.append('site_types', f.siteTypes.join(','));
      }
      if (f.casino) params.append('casino', 'true');
      if (f.cbd) params.append('cbd', 'true');
      if (f.gambling) params.append('gambling', 'true');
      if (f.cryptocurrency) params.append('cryptocurrency', 'true');
      if (f.sex) params.append('sex', 'true');
      if (f.erotic) params.append('erotic', 'true');
      if (f.vpn) params.append('vpn', 'true');
      if (f.tarot) params.append('tarot', 'true');
      if (f.porn) params.append('porn', 'true');
      if (f.cannabis) params.append('cannabis', 'true');
      if (f.dr_min) params.append('dr_min', f.dr_min);
      if (f.dr_max) params.append('dr_max', f.dr_max);
      if (f.traffic_min) params.append('traffic_min', f.traffic_min);
      if (f.traffic_max) params.append('traffic_max', f.traffic_max);
      if (f.price_min) params.append('price_min', f.price_min);
      if (f.price_max) params.append('price_max', f.price_max);
      if (sortField) {
        params.append('sort_by', sortField);
        params.append('sort_order', sortOrder);
      }

      const response = await axios.get(`${API}/products?${params.toString()}`);
      setProducts(response.data.products);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  }, [filters, itemsPerPage, sortField, sortOrder]);

  useEffect(() => {
    fetchProducts(1);
  }, []);

  useEffect(() => {
    if (sortField) {
      fetchProducts(pagination.page);
    }
  }, [sortField, sortOrder]);

  // Auto-apply filters when they change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProducts(1);
    }, 300); // Small debounce for numeric inputs
    return () => clearTimeout(timeoutId);
  }, [filters]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleClearFilters = () => {
    const emptyFilters = {
      search: '',
      countries: [],
      categories: [],
      siteTypes: [],
      casino: false,
      cbd: false,
      gambling: false,
      cryptocurrency: false,
      sex: false,
      erotic: false,
      vpn: false,
      tarot: false,
      porn: false,
      cannabis: false,
      dr_min: '',
      dr_max: '',
      traffic_min: '',
      traffic_max: '',
      price_min: '',
      price_max: '',
    };
    setFilters(emptyFilters);
  };

  const handlePageChange = (newPage) => {
    fetchProducts(newPage);
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    fetchProducts(1, null, newLimit);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const openProductDialog = (product) => {
    setSelectedProduct(product);
    setProductDialogOpen(true);
  };

  const handleCountryToggle = (country) => {
    const current = filters.countries || [];
    const newCountries = current.includes(country)
      ? current.filter(c => c !== country)
      : [...current, country];
    handleFilterChange('countries', newCountries);
  };

  const handleCategoryToggle = (category) => {
    const current = filters.categories || [];
    const newCategories = current.includes(category)
      ? current.filter(c => c !== category)
      : [...current, category];
    handleFilterChange('categories', newCategories);
  };

  // Download filtered results
  const handleDownloadFiltered = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.countries && filters.countries.length > 0) {
        params.append('countries', filters.countries.join(','));
      }
      if (filters.categories && filters.categories.length > 0) {
        params.append('categories', filters.categories.join(','));
      }
      if (filters.siteTypes && filters.siteTypes.length > 0) {
        params.append('site_types', filters.siteTypes.join(','));
      }
      if (filters.dr_min) params.append('dr_min', filters.dr_min);
      if (filters.dr_max) params.append('dr_max', filters.dr_max);
      if (filters.traffic_min) params.append('traffic_min', filters.traffic_min);
      if (filters.traffic_max) params.append('traffic_max', filters.traffic_max);
      if (filters.price_min) params.append('price_min', filters.price_min);
      if (filters.price_max) params.append('price_max', filters.price_max);
      
      const sensitiveTopics = ['casino', 'cbd', 'gambling', 'cryptocurrency', 'sex', 'erotic', 'vpn', 'tarot', 'porn', 'cannabis'];
      sensitiveTopics.forEach(topic => {
        if (filters[topic]) params.append(topic, 'true');
      });

      const response = await axios.get(`${API}/products/export?${params.toString()}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `catalogo_cliente_filtrado_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(tx('Excel descargado correctamente', 'Excel downloaded successfully'));
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error(tx('Error al descargar', 'Download error'));
    } finally {
      setDownloading(false);
    }
  };

  // Count active filters
  const activeFiltersCount = () => {
    let count = 0;
    if (filters.search) count++;
    if (filters.countries && filters.countries.length > 0) count += filters.countries.length;
    if (filters.categories && filters.categories.length > 0) count += filters.categories.length;
    if (filters.casino) count++;
    if (filters.cbd) count++;
    if (filters.gambling) count++;
    if (filters.cryptocurrency) count++;
    if (filters.sex) count++;
    if (filters.erotic) count++;
    if (filters.vpn) count++;
    if (filters.tarot) count++;
    if (filters.porn) count++;
    if (filters.cannabis) count++;
    if (filters.dr_min) count++;
    if (filters.dr_max) count++;
    if (filters.traffic_min) count++;
    if (filters.traffic_max) count++;
    if (filters.price_min) count++;
    if (filters.price_max) count++;
    return count;
  };

  const filteredCountries = uniqueCountries.filter(country =>
    country.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const filteredCategories = uniqueCategories.filter(category =>
    category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const sensitiveTopics = [
    { key: 'casino', label: t.casino },
    { key: 'cbd', label: t.cbd },
    { key: 'gambling', label: t.gambling },
    { key: 'cryptocurrency', label: t.cryptocurrency },
    { key: 'sex', label: t.sex },
    { key: 'erotic', label: t.erotic },
    { key: 'vpn', label: t.vpn },
    { key: 'tarot', label: t.tarot },
    { key: 'porn', label: t.porn },
    { key: 'cannabis', label: t.cannabis },
  ];

  return (
    <div className="space-y-6" data-testid="client-catalog-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Eye className="h-6 w-6 text-primary" />
            {t.title}
          </h2>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={handleDownloadFiltered} 
            disabled={downloading}
            className="bg-green-600 hover:bg-green-700"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t.downloading}
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t.downloadFiltered}
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Eye className="h-5 w-5 text-blue-600" />
            <p className="text-blue-700 text-sm">
              {tx('Esta vista muestra exactamente lo que ven los clientes: solo productos disponibles con precios PVP. Puedes filtrar y descargar el Excel con los mismos criterios.',
                 'This view shows exactly what clients see: only available products with PVP prices. You can filter and download Excel with the same criteria.')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Filters Row */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t.search}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Country Multi-Select */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[150px] justify-between">
                  <Globe className="h-4 w-4 mr-2" />
                  {filters.countries?.length > 0 ? (
                    <span>{filters.countries.length} {t.countries}</span>
                  ) : (
                    <span>{t.allCountries}</span>
                  )}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2">
                <Input
                  placeholder={t.searchCountry}
                  value={countrySearch}
                  onChange={(e) => setCountrySearch(e.target.value)}
                  className="mb-2"
                />
                <ScrollArea className="h-48">
                  {filteredCountries.map(country => (
                    <div key={country} className="flex items-center space-x-2 p-1">
                      <Checkbox
                        id={`country-${country}`}
                        checked={filters.countries?.includes(country)}
                        onCheckedChange={() => handleCountryToggle(country)}
                      />
                      <Label htmlFor={`country-${country}`} className="cursor-pointer text-sm">
                        {translateCountry(country, language)}
                      </Label>
                    </div>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* Category Multi-Select */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="min-w-[150px] justify-between">
                  <FolderOpen className="h-4 w-4 mr-2" />
                  {filters.categories?.length > 0 ? (
                    <span>{filters.categories.length} {t.categories}</span>
                  ) : (
                    <span>{t.allCategories}</span>
                  )}
                  <ChevronDown className="h-4 w-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-2">
                <Input
                  placeholder={t.searchCategory}
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="mb-2"
                />
                <ScrollArea className="h-48">
                  {filteredCategories.map(category => (
                    <div key={category} className="flex items-center space-x-2 p-1">
                      <Checkbox
                        id={`category-${category}`}
                        checked={filters.categories?.includes(category)}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <Label htmlFor={`category-${category}`} className="cursor-pointer text-sm">
                        {translateCategory(category, language)}
                      </Label>
                    </div>
                  ))}
                </ScrollArea>
              </PopoverContent>
            </Popover>

            {/* Site Type Filter */}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-between font-normal">
                  {filters.siteTypes?.length > 0 
                    ? `${filters.siteTypes.length} tipos`
                    : tx('Tipo Medio', 'Site Type')}
                  <ChevronDown className="h-4 w-4 ml-2 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[200px] p-2" align="start">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium">{tx('Tipo de Medio', 'Site Type')}</span>
                  {filters.siteTypes?.length > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs"
                      onClick={() => handleFilterChange('siteTypes', [])}
                    >
                      {tx('Limpiar', 'Clear')}
                    </Button>
                  )}
                </div>
                <div className="space-y-1">
                  {['Periódico', 'Web/Blog', 'Blog', 'Web'].map((siteType) => (
                    <div key={siteType} className="flex items-center space-x-2 p-1.5 rounded hover:bg-muted">
                      <Checkbox
                        id={`site-type-${siteType}`}
                        checked={filters.siteTypes?.includes(siteType)}
                        onCheckedChange={(checked) => {
                          const currentTypes = filters.siteTypes || [];
                          const newTypes = checked
                            ? [...currentTypes, siteType]
                            : currentTypes.filter(t => t !== siteType);
                          handleFilterChange('siteTypes', newTypes);
                        }}
                      />
                      <Label htmlFor={`site-type-${siteType}`} className="text-sm cursor-pointer flex-1">
                        {siteType}
                      </Label>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>

            {/* DR Range */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">DR:</span>
              <Input
                type="number"
                placeholder={t.drMin}
                value={filters.dr_min}
                onChange={(e) => handleFilterChange('dr_min', e.target.value)}
                className="w-20"
              />
              <span>-</span>
              <Input
                type="number"
                placeholder={t.drMax}
                value={filters.dr_max}
                onChange={(e) => handleFilterChange('dr_max', e.target.value)}
                className="w-20"
              />
            </div>

            {/* Traffic Range */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{tx('Tráfico', 'Traffic')}:</span>
              <Input
                type="number"
                placeholder={t.trafficMin}
                value={filters.traffic_min}
                onChange={(e) => handleFilterChange('traffic_min', e.target.value)}
                className="w-24"
              />
              <span>-</span>
              <Input
                type="number"
                placeholder={t.trafficMax}
                value={filters.traffic_max}
                onChange={(e) => handleFilterChange('traffic_max', e.target.value)}
                className="w-24"
              />
            </div>

            {/* Price Range */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{t.price}:</span>
              <Input
                type="number"
                placeholder={t.minPrice}
                value={filters.price_min}
                onChange={(e) => handleFilterChange('price_min', e.target.value)}
                className="w-24"
              />
              <span>-</span>
              <Input
                type="number"
                placeholder={t.maxPrice}
                value={filters.price_max}
                onChange={(e) => handleFilterChange('price_max', e.target.value)}
                className="w-24"
              />
            </div>
          </div>

          {/* Sensitive Topics Row */}
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t">
            <span className="text-sm text-muted-foreground mr-2">{t.sensitiveTopics}:</span>
            {sensitiveTopics.map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-1">
                <Checkbox
                  id={`sensitive-${key}`}
                  checked={filters[key]}
                  onCheckedChange={(checked) => handleFilterChange(key, checked)}
                />
                <Label htmlFor={`sensitive-${key}`} className="cursor-pointer text-sm">
                  {label}
                </Label>
              </div>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-4 pt-4 border-t">
            {activeFiltersCount() > 0 && (
              <Button variant="outline" onClick={handleClearFilters}>
                <RotateCcw className="h-4 w-4 mr-2" />
                {t.clearFilters}
                <Badge variant="secondary" className="ml-2">{activeFiltersCount()}</Badge>
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Results Info */}
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground">
          {t.showing} <strong>{products.length}</strong> {tx('de', 'of')} <strong>{pagination.total}</strong> {t.results}
        </p>
      </div>

      {/* Product Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : products.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-lg text-muted-foreground">{t.noResults}</p>
            <p className="text-sm text-muted-foreground mt-2">{t.tryOther}</p>
          </CardContent>
        </Card>
      ) : (
        <ProductTable
          products={products}
          onProductClick={openProductDialog}
          onSort={handleSort}
          sortField={sortField}
          sortOrder={sortOrder}
          language={language}
        />
      )}

      {/* Pagination */}
      {products.length > 0 && (
        <PaginationControls
          pagination={pagination}
          onPageChange={handlePageChange}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          language={language}
        />
      )}

      {/* Product Detail Dialog */}
      <ProductDetailDialog
        product={selectedProduct}
        open={productDialogOpen}
        onOpenChange={setProductDialogOpen}
        onFindSimilar={() => {}}
        language={language}
      />
    </div>
  );
};

export default ClientCatalogTab;
